import React, { useEffect, useRef, useState, useCallback } from "react";
// @ts-ignore
import { tw } from "@careinfo/tailwind";
import Reply from "../../Images/Reply";
import Forward from "../../Images/Forward";
import Dropdown from "../../al_Image/Dropdown";
import { postFileApiData } from "../../helpers/AxiosInstance";
import DOMPurify from "dompurify";
import useGetHook from "../../hooks/useGetHook";
import usePostHook from "../../hooks/usePostHook";
import usePostFormDataHook from "../../hooks/usePostFormDataHook";
import useMoveOrDeleteEmailHook from "../../hooks/useMoveOrDeleteEmailHook";
import APIS from "../../constant/Endpoint";
import moment from "moment";
import { toast } from "react-toastify";
import { useEditor, EditorContent, Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import "../emailBody.css";
import { useLucideIcon } from "../../Icons/lucide-react/index";
import {
  UploadedFile,
  ComposeMode,
  AttachedFile,
  UploadedFileMetadata,
  PreviewFile,
} from "./types";
import { generateFileId } from "../shared/helpers";
import { EditorButton } from "../shared/EditorButton";
import { AttachmentCard } from "../shared/AttachmentCard";
import { ConfirmModal } from "./ConfirmModal";
import { PreviewModal } from "../shared/PreviewModal";
import useMoveToMailBox from "../../hooks/useMoveToMailBox";
import { useLocation } from "react-router-dom";
import { getEmailFolderId } from "../../layouts/DashboardLayout";
import { EMAIL_REGEX } from "../shared/constants";
import { Eye } from "lucide-react";
//@ts-ignore
import linkifyHtml from "linkify-html";
// Email suggestion dropdown component

const EmailSuggestions = ({
  suggestions,
  onSelect,
  selectedIndex,
  inputRef,
}) => {
  const dropdownRef = useRef(null);
  useEffect(() => {
    if (dropdownRef.current && selectedIndex >= 0) {
      const selectedElement = dropdownRef.current.children[selectedIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: "nearest" });
      }
    }
  }, [selectedIndex]);
  if (suggestions.length === 0) return null;
  return (
    <div
      ref={dropdownRef}
      className={tw`absolute z-50 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto w-full mt-1 left-0 top-full`}
    >
      {(suggestions ?? []).map((contact, index) => (
        <div
          key={contact.id}
          onMouseDown={(e) => onSelect(contact.email)}
          className={tw`px-3 py-2 cursor-pointer hover:bg-gray-100 border ${
            index === selectedIndex ? "bg-gray-100" : ""
          }`}
        >
          <div className={tw`text-sm font-medium text-gray-900`}>
            {contact.name || contact.email}
          </div>
          {contact.name && (
            <div className={tw`text-xs text-gray-500`}>{contact.email}</div>
          )}
        </div>
      ))}
    </div>
  );
};

const MailDetail = ({ selectedEmailID, onClose }) => {
  const [showReplyBox, setShowReplyBox] = useState(false);
  const [showForwardBox, setShowForwardBox] = useState(false);
  const [composeMode, setComposeMode] = useState<ComposeMode>(null);
  const [toEmails, setToEmails] = useState<string[]>([]);
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [bccEmails, setBccEmails] = useState<string[]>([]);
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [sending, setSending] = useState(false);
  const [downloadingFiles, setDownloadingFiles] = useState<Set<number>>(
    new Set(),
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [previewFile, setPreviewFile] = useState<PreviewFile | null>(null);

  // Autocomplete state
  const [recipientInput, setRecipientInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const recipientInputRef = useRef<HTMLInputElement | null>(null);

  const location = useLocation();
  const foldersId: any = getEmailFolderId();
  const {
    List,
    Paperclip,
    ListOrdered,
    TextBold,
    TextItalic,
    TextUnderline,
    Trash2,
    Download,
    FileIcon,
    Inbox,
  } = useLucideIcon();
  const editor: Editor | null = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder: "Write your reply here...",
      }),
      Underline,
      Image.configure({
        inline: true,
        resize: {
          enabled: true,
          alwaysPreserveAspectRatio: true,
        },
      }),
    ],
    editorProps: {
      attributes: {
        class: "pt-2 min-h-[300px] focus:outline-none",
      },
    },
    shouldRerenderOnTransaction: true,
    content: "",
  });
  const handleCleanup = () => {
    const editorContent = editor?.getHTML();
    if (editorContent && editorContent !== "<p></p>") {
      setShowConfirmModal(true);
    }
  };
  const handleConfirmClear = () => {
    editor.commands.setContent(`<p></p>${signature?.signature || ""}`);
    editor.commands.focus("start");
    setShowConfirmModal(false);
  };
  const handleCancelClear = () => {
    setShowConfirmModal(false);
  };
  const { data, isLoading } = useGetHook({
    queryKey: [`mail-detail-${selectedEmailID}`],
    url: selectedEmailID ? `${APIS.EMAIL}${selectedEmailID}/` : null,
    params: {},
  });
  const { mutateAsync: uploadFileMutation } = usePostFormDataHook({
    queryKey: ["upload-file"],
  });
  const { mutateAsync: moveOrDeleteEmailMutation } = useMoveOrDeleteEmailHook({
    queryKey: ["delete-list"],
  });
  const { mutateAsync: MoveToMailBox } = useMoveToMailBox({
    queryKey: ["move-list"],
  });
  const { data: contact, isLoading: contactLoading } = useGetHook({
    queryKey: [`contact`],
    url: `${APIS.CONTACTS}`,
    params: {},
  });
  // Filter contacts based on input
  const filterContacts = useCallback(
    (inputValue: string) => {
      if (!inputValue || !contact) return [];

      const searchTerm = inputValue.toLowerCase();
      return contact
        .filter((c) => {
          const email = c.email?.toLowerCase() || "";
          const name = c.name?.toLowerCase() || "";
          return email.includes(searchTerm) || name.includes(searchTerm);
        })
        .slice(0, 5);
    },
    [contact],
  );
  // Watch input changes for autocomplete
  useEffect(() => {
    if (recipientInput) {
      setSuggestions(filterContacts(recipientInput));
      setSelectedIndex(-1);
    } else {
      setSuggestions([]);
    }
  }, [recipientInput, filterContacts]);
  const handleMoveToMailBox = async () => {
    try {
      const uploadUrl = `${APIS.EMAIL}move-to-mailbox/`;
      const response = await MoveToMailBox({
        url: uploadUrl,
        email_ids: [selectedEmailID],
        target_mailbox_id: foldersId?.Inbox,
      });
      if (response.status === 200) {
        onClose();
      }
    } catch (error) {
      console.log(error);
    }
  };
  const handleMoveOrDelete = async () => {
    try {
      const uploadUrl = `${APIS.EMAIL}move-or-delete/`;
      const response = await moveOrDeleteEmailMutation({
        url: uploadUrl,
        email_ids: [selectedEmailID],
        action: "trash",
      });
      if (response.status === 200) {
        onClose();
      }
    } catch (error) {
      console.log(error);
    }
  };
  const { mutateAsync: sendMail } = usePostHook({
    queryKey: ["send-mail"],
  });
  const mail = data?.email;
  const attachments =
    mail?.bodyStructure?.subParts?.filter(
      (p) => p.disposition === "attachment",
    ) ?? [];
  const preview =
    mail?.preview ||
    mail?.textBody?.[0]?.content ||
    mail?.htmlBody?.[0]?.content ||
    "";
  const sender = mail?.from?.[0];
  const recipient = mail?.to?.[0];
  const letter =
    sender?.name?.[0]?.toUpperCase() ||
    sender?.email?.[0]?.toUpperCase() ||
    "?";
  const uploadFile = async (
    file: File,
  ): Promise<UploadedFileMetadata | null> => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const uploadUrl = `${APIS.EMAIL}file-upload/`;
      const response = await uploadFileMutation({
        url: uploadUrl,
        formData,
      });
      if (response?.status === 201) {
        const data = response.data;
        return {
          blobId: data.blobId,
          name: data.name || file.name,
          size: data.size || file.size,
          type: data.type || file.type,
        };
      }
      console.error("File upload failed:", response);
      return null;
    } catch (error: any) {
      console.error("File upload error:", error);
      return null;
    }
  };
  const handleFileClick = () => fileInputRef.current?.click();
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const newFiles: AttachedFile[] = files.map((file) => ({
      file,
      id: generateFileId(file.name),
      uploading: true,
    }));
    setAttachedFiles((prev) => [...prev, ...newFiles]);
    if (fileInputRef.current) fileInputRef.current.value = "";
    for (let i = 0; i < newFiles.length; i++) {
      const uploadedMetadata = await uploadFile(newFiles[i].file);
      setAttachedFiles((prev) =>
        prev.map((af) =>
          af.id === newFiles[i].id
            ? {
                ...af,
                uploading: false,
                blobId: uploadedMetadata?.blobId,
                name: uploadedMetadata?.name,
                size: uploadedMetadata?.size,
                type: uploadedMetadata?.type,
                uploadError: uploadedMetadata ? undefined : "Upload failed",
              }
            : af,
        ),
      );
    }
  };
  const removeAttachment = (id: string) => {
    setAttachedFiles((prev) => prev.filter((f) => f.id !== id));
  };
  const addEmail = (email: string) => {
    const trimmed = email.trim();
    if (trimmed && EMAIL_REGEX.test(trimmed) && !toEmails.includes(trimmed)) {
      setToEmails([...toEmails, trimmed]);
      setRecipientInput("");
      setSuggestions([]);
    }
  };
  const removeEmail = (
    email: string,
    listSetter: React.Dispatch<React.SetStateAction<string[]>>,
  ) => {
    listSetter((prev) => prev.filter((e) => e !== email));
  };
  const handleSuggestionSelect = useCallback(
    (email: string) => {
      if (email && !toEmails.includes(email)) {
        setToEmails((prev) => [...prev, email]);
        setRecipientInput("");
        setSuggestions([]);
      }
    },
    [toEmails],
  );
  const handleRecipientKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Handle arrow key navigation
      if (suggestions.length > 0) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < suggestions.length - 1 ? prev + 1 : prev,
          );
          return;
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          return;
        } else if (e.key === "Enter" && selectedIndex >= 0) {
          e.preventDefault();
          handleSuggestionSelect(suggestions[selectedIndex].email);
          return;
        } else if (e.key === "Escape") {
          setSuggestions([]);
          setSelectedIndex(-1);
          return;
        }
      }
      // Original key handling
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        console.log("before recipientInput", recipientInput);

        if (recipientInput.trim()) {
          console.log("after recipientInput", recipientInput);

          addEmail(recipientInput);
        }
      } else if (
        e.key === "Backspace" &&
        recipientInput === "" &&
        toEmails.length > 0
      ) {
        setToEmails((prev) => prev.slice(0, -1));
      }
    },
    [
      suggestions,
      selectedIndex,
      recipientInput,
      toEmails,
      handleSuggestionSelect,
    ],
  );
  const handleRecipientBlur = useCallback(() => {
    if (recipientInput.trim()) {
      addEmail(recipientInput);
    }
    setSuggestions([]);
  }, [recipientInput]);

  const handlePreviewLocal = (attachedFile: AttachedFile) => {
    try {
      const url = URL.createObjectURL(attachedFile.file);
      setPreviewFile({
        url,
        type: attachedFile.type,
        name: attachedFile.name,
      });
    } catch (err) {
      console.error("Local preview error:", err);
      toast.error("Failed to generate preview");
    }
  };
  const handleSend = async () => {
    const stillUploading = attachedFiles.some((af) => af.uploading);
    if (stillUploading) {
      alert("Please wait for all files to finish uploading");
      return;
    }
    const failedUploads = attachedFiles.filter(
      (af) => af.uploadError || !af.blobId,
    );
    if (failedUploads.length > 0) {
      alert("Some files failed to upload. Please remove them and try again.");
      return;
    }
    let replyBody = editor?.getHTML();
    let to = toEmails;
    let subject = mail.subject;
    let body = replyBody;
    if (composeMode === "reply") {
      if (!sender?.email) return alert("Sender email not found");
      to = [sender.email];
      subject = mail.subject?.startsWith("Re:")
        ? mail.subject
        : `Re: ${mail.subject}`;
    }
    if (composeMode === "forward") {
      if (to.length === 0) return alert("Enter recipient(s) email(s)");
      subject = mail.subject?.startsWith("Fwd:")
        ? mail.subject
        : `Fwd: ${mail.subject}`;
      body = `---------- Forwarded message ----------
From: ${sender?.email}
Date: ${moment(mail.receivedAt).format("DD/MM/YYYY HH:mm")}
Subject: ${mail.subject}
To: ${recipient?.email}
${preview}
${replyBody ? "\n" + replyBody : ""}`;
    }
    const formData = {
      to,
      cc: ccEmails.length > 0 ? ccEmails : [],
      bcc: bccEmails.length > 0 ? bccEmails : [],
      subject,
      html_body: body,
      attachments:
        attachedFiles.length > 0
          ? attachedFiles.map((af) => ({
              blobId: af.blobId,
              name: af.name || af.file.name,
              size: af.size || af.file.size,
              type: af.type || af.file.type,
            }))
          : [],
      original_email_id: mail?.id,
    };
    console.log(
      "Sending email with payload:",
      JSON.stringify(formData, null, 2),
    );
    setSending(true);
    try {
      const res = await sendMail({
        url: APIS.EMAIL,
        formData,
        setError: () => {},
      });
      if (res?.status === 200 || res?.status === 201) {
        setShowReplyBox(false);
        setShowForwardBox(false);
        setComposeMode(null);
        setToEmails([]);
        setCcEmails([]);
        setBccEmails([]);
        setAttachedFiles([]);
        setRecipientInput("");
        editor.commands.setContent(`<p></p>${signature?.signature || ""}`);
      }
    } catch (err) {
      console.error("Send error:", err);
      alert("Failed to send email. Please try again.");
    } finally {
      setSending(false);
    }
  };
  const handleDownloadAttachment = async (
    file: UploadedFile,
    index: number,
  ) => {
    setDownloadingFiles((prev) => new Set(prev).add(index));
    const url = `${APIS.EMAIL}attachment-download/`;
    const formData = new FormData();
    formData.append("blob_id", file.blobId);
    formData.append("filename", file.name);
    try {
      const blob = await postFileApiData({
        url,
        formData,
        responseType: "blob",
      });
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download error:", err);
      toast.error("Failed to download file");
    } finally {
      setDownloadingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });
    }
  };
  useEffect(() => {
    setShowReplyBox(false);
    setComposeMode(null);
    setAttachedFiles([]);
    setDownloadingFiles(new Set());
    setToEmails([]);
    setRecipientInput("");
    setSuggestions([]);
    setIsDropdownOpen(false);
    if (editor && signature && !isSignatureLoading) {
      editor.commands.setContent(`<p></p>${signature?.signature || ""}`);
    }
  }, [selectedEmailID]);
  const { data: signature, isLoading: isSignatureLoading } = useGetHook({
    queryKey: [`signature`],
    url: `${APIS.EMAIL}signature/`,
    params: {},
  });
  useEffect(() => {
    if (editor && signature && !isSignatureLoading) {
      const sigContent = signature?.signature || "";
      const initialContent = `<p></p>${sigContent}`;
      if (editor.getHTML() === "<p></p>") {
        editor.commands.setContent(initialContent);
      }
    }
  }, [editor, signature, isSignatureLoading]);
  if (!selectedEmailID) {
    return (
      <div className={tw`p-4 text-center text-gray-500`}>
        Select an email to view details
      </div>
    );
  }
  if (!editor) {
    return (
      <div className={tw`flex items-center justify-center p-8`}>
        <p className={tw`text-gray-500`}>Loading editor...</p>
      </div>
    );
  }
  if (isLoading || !mail) {
    return (
      <div className={tw`p-10 flex justify-center items-center`}>
        <div
          className={tw`animate-spin h-10 w-10 border-4 border-primary border-t-transparent rounded-full`}
        />
      </div>
    );
  }
  const rawBody =
    mail?.bodyValues?.["2"]?.value ||
    mail?.bodyValues?.["0"]?.value ||
    mail?.bodyValues?.["1"]?.value ||
    "";

  const processedBody = linkifyHtml(rawBody, {
    target: "_blank",
    rel: "noopener noreferrer",
    className: tw`text-blue-600 underline hover:text-blue-700`,
  });

   const sanitizedBody = DOMPurify.sanitize(processedBody, {
    ADD_ATTR: ["target", "rel"],
  });

  return (
    <div
      className={tw`w-full flex flex-col bg-white sticky top-1 max-h-[80vh] overflow-y-auto`}
    >
      <ConfirmModal
        isOpen={showConfirmModal}
        onConfirm={handleConfirmClear}
        onCancel={handleCancelClear}
        title="Clear Reply Box?"
        message="Are you sure you want to clear the reply box? All content will be lost and this action cannot be undone."
      />
      <PreviewModal
        isOpen={!!previewFile}
        onClose={() => setPreviewFile(null)}
        url={previewFile?.url || ""}
        name={previewFile?.name || ""}
        type={previewFile?.type || ""}
        file={attachedFiles.find((f) => f.name === previewFile?.name)?.file}
      />
      <div className={tw`border-b flex items-center gap-6 p-3 justify-between`}>
        <div className="flex gap-x-4">
          <div className={tw`text-yellow-300 text-2xl`}>★</div>
          <button
            className={tw`flex items-center gap-2 cursor-pointer`}
            onClick={() => {
              setComposeMode("reply");
              setShowForwardBox(false);
              setShowReplyBox(!showReplyBox);
            }}
          >
            <Reply /> <span>Reply</span>
          </button>
          <button
            className={tw`flex items-center gap-2 cursor-pointer`}
            onClick={() => {
              setComposeMode("forward");
              setShowReplyBox(false);
              setShowForwardBox(!showForwardBox);
            }}
          >
            <Forward /> <span>Forward</span>
          </button>
          {(location.pathname === `/mail/${foldersId?.Deleted}` ||
            location.pathname === `/mail/${foldersId?.Junk}`) && (
            <div
              onClick={handleMoveToMailBox}
              className={tw`flex items-center justify-center cursor-pointer gap-1 transition font-medium hover:text-red-400 p-1`}
            >
              <Inbox size={17} />
              <span>Move to Inbox</span>
            </div>
          )}
          <div
            onClick={handleMoveOrDelete}
            className={tw`flex items-center justify-center gap-1 cursor-pointer transition font-medium hover:text-red-400 p-1`}
          >
            <Trash2 size={17} />
          </div>
        </div>
        <button
          onClick={() => {
            setShowReplyBox(false);
            setComposeMode(null);
            setAttachedFiles([]);
            setRecipientInput("");
            editor?.commands.setContent(`<p></p>${signature?.signature || ""}`);
            onClose();
          }}
          className={tw`text-xl text-gray-400 hover:text-red-500`}
        >
          ×
        </button>
      </div>
      <div className={tw`border-b flex items-start gap-4 p-6 bg-gray-50`}>
        <div
          className={tw`w-14 h-14 rounded-full flex items-center justify-center bg-gradient-to-br from-primary to-red-600 text-white text-xl font-bold shadow-sm flex-shrink-0`}
        >
          {letter}
        </div>
        <div className={tw`flex flex-col flex-1 min-w-0`}>
          <h2 className={tw`text-lg font-semibold text-gray-900 truncate`}>
            {sender?.name || sender?.email}
          </h2>
          <p className={tw`text-sm text-gray-600 truncate mb-2`}>
            {sender?.email}
          </p>
          <div className={tw`flex items-center gap-2 text-sm text-gray-600`}>
            <button
              type="button"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className={tw`flex items-center gap-1 hover:text-gray-900 transition-colors focus:outline-none`}
            >
              <Dropdown />
            </button>
            <span className={tw`text-gray-500`}>to</span>
            <span className={tw`text-gray-900 truncate`}>
              {recipient?.email}
            </span>
          </div>
          {isDropdownOpen && (
            <div
              className={tw`mt-3 p-3 bg-white border border-gray-200 rounded-lg text-sm space-y-2 shadow-sm `}
            >
              <div className={tw`flex gap-2`}>
                <span className={tw`font-medium text-gray-700 min-w-[10px]`}>
                  From:
                </span>
                <span className={tw`text-gray-900`}>
                  {sender?.name} {`<${sender?.email}>`}
                </span>
              </div>
              <div className={tw`flex gap-2`}>
                <span className={tw`font-medium text-gray-700 min-w-[10px] `}>
                  To:
                </span>
                <span className={tw`text-gray-900`}>{recipient?.email}</span>
              </div>
              {mail?.cc && mail?.cc.length > 0 && (
                <div className={tw`flex gap-2`}>
                  <span className={tw`font-medium text-gray-700 min-w-[10px]`}>
                    Cc:
                  </span>
                  <span className={tw`text-gray-900`}>
                    {mail?.cc?.map((c: any, idx: number) => (
                      <span key={idx}>
                        {c.email}
                        {idx < mail.cc.length - 1 ? ", " : ""}
                      </span>
                    ))}
                  </span>
                </div>
              )}
              {mail?.bcc && mail?.bcc.length > 0 && (
                <div className={tw`flex gap-2`}>
                  <span className={tw`font-medium text-gray-700 min-w-[10px]`}>
                    Bcc:
                  </span>
                  <span className={tw`text-gray-900`}>
                    {mail?.bcc?.map((b: any, idx: number) => (
                      <span key={idx}>
                        {b.email}
                        {idx < mail.bcc.length - 1 ? ", " : ""}
                      </span>
                    ))}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
        <div className={tw`text-right text-sm text-gray-500 flex-shrink-0`}>
          <p className={tw`font-medium text-gray-700`}>
            {moment(mail.receivedAt).format("DD/MM/YYYY")}
          </p>
          <p className={tw`text-gray-500 mt-1`}>
            {moment(mail.receivedAt).format("HH:mm")}
          </p>
        </div>
      </div>
      <div className={tw`p-5 border-b flex-1 min-h-0 overflow-y-auto`}>
        <h2 className={tw`text-lg font-semibold mb-3 text-gray-900`}>
          {mail.subject}
        </h2>
        <div
          className={tw`text-gray-700 text-base leading-relaxed email-body`}
          dangerouslySetInnerHTML={{__html: sanitizedBody}}
        onClick={(e)=>{
         const anchor = (e.target as HTMLElement).closest("a");
            if (anchor && anchor.getAttribute("target") === "_blank") {
              window.open(
                anchor.getAttribute("href"),
                "_blank",
                "noopener,noreferrer",
              );
              e.preventDefault();
            }
          }}
        />
      </div>
      {attachments.length > 0 && (
        <div className={tw`border-b p-5 flex flex-col gap-3`}>
          <h3 className={tw`text-sm font-semibold`}>Attachments:</h3>
          {attachments.map((a: UploadedFile, idx: number) => (
            <div
              key={idx}
              className={tw`flex items-center justify-between gap-3 text-sm text-gray-700 bg-gray-50 p-3 rounded-md w-[70%]`}
            >
              <div className={tw`flex items-center gap-3`}>
                <FileIcon size={18} className={tw`text-gray-600`} />
                <span>{a.name}</span>
              </div>
              <div className={tw`flex gap-2`}>
                <button
                  onClick={() => handleDownloadAttachment(a, idx)}
                  disabled={downloadingFiles.has(idx)}
                  className={tw`flex items-center gap-2 bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 transition-colors ${
                    downloadingFiles.has(idx)
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                >
                  {downloadingFiles.has(idx) ? (
                    <>
                      <div
                        className={tw`animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full`}
                      />
                    </>
                  ) : (
                    <>
                      <Download size={16} />
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {(showReplyBox || showForwardBox) && (
        <div className={tw`flex flex-col`}>
          {composeMode === "forward" && (
            <div className={tw`p-4 border-b flex flex-wrap gap-1 relative`}>
              {toEmails.map((email) => (
                <span
                  key={email}
                  className={tw`bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm flex items-center gap-1`}
                >
                  {email}
                  <button
                    onClick={() => removeEmail(email, setToEmails)}
                    className={tw`text-blue-600`}
                  >
                    ×
                  </button>
                </span>
              ))}
              <input
                type="text"
                value={recipientInput}
                onChange={(e) => setRecipientInput(e.target.value)}
                onKeyDown={handleRecipientKeyDown}
                onBlur={handleRecipientBlur}
                placeholder={toEmails.length === 0 ? "Recipients" : ""}
                className={tw`flex-1 min-w-[120px] p-2 border rounded focus:outline-none`}
                ref={recipientInputRef}
              />
              <EmailSuggestions
                suggestions={suggestions}
                onSelect={(email) => handleSuggestionSelect(email)}
                selectedIndex={selectedIndex}
                inputRef={recipientInputRef}
              />
            </div>
          )}

          <div className={tw`border-b p-5`}>
            <EditorContent editor={editor} id="replyField" />
          </div>

          {attachedFiles.length > 0 && (
            <div className={tw`px-4 py-3 border-b bg-gray-50`}>
              <div className={tw`flex flex-wrap gap-2`}>
                {attachedFiles.map((file) => (
                  <AttachmentCard
                    key={file.id}
                    file={file}
                    onRemove={removeAttachment}
                    onPreview={handlePreviewLocal}
                  />
                ))}
              </div>
            </div>
          )}

          <div className={tw`flex justify-between items-center p-5 border-t`}>
            <div className={tw`flex items-center gap-4 text-gray-600`}>
              <div
                onClick={handleFileClick}
                className={tw`flex items-center cursor-pointer rounded-md border transition font-medium border-gray-300 hover:text-red-400 p-1`}
              >
                <Paperclip size={17} />
              </div>
              <EditorButton
                isMailDetail={true}
                title="Bold (Ctrl+B)"
                isActive={editor.isActive("bold")}
                onClick={() => editor.chain().focus().toggleBold().run()}
                icon={<TextBold />}
              />
              <EditorButton
                isMailDetail={true}
                title="Italic (Ctrl+I)"
                isActive={editor.isActive("italic")}
                onClick={() => editor.chain().focus().toggleItalic().run()}
                icon={<TextItalic />}
              />
              <EditorButton
                isMailDetail={true}
                title="Underline (Ctrl+U)"
                isActive={editor.isActive("underline")}
                onClick={() => editor.chain().focus().toggleUnderline().run()}
                icon={<TextUnderline />}
              />
              <EditorButton
                isMailDetail={true}
                title="Numbered list (Ctrl+Shift+7)"
                isActive={editor.isActive("orderedList")}
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                icon={<ListOrdered />}
              />
              <EditorButton
                isMailDetail={true}
                title="Bulleted list (Ctrl+Shift+8)"
                isActive={editor.isActive("bulletList")}
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                icon={<List />}
              />

              <div
                onClick={handleCleanup}
                className={tw`flex items-center cursor-pointer rounded-md border transition font-medium border-gray-300 hover:text-red-400 p-1`}
              >
                <Trash2 size={17} />
              </div>
            </div>

            <input
              type="file"
              multiple
              ref={fileInputRef}
              onChange={handleFileChange}
              className={tw`hidden`}
            />

            <button
              onClick={handleSend}
              disabled={sending || attachedFiles.some((f) => f.uploading)}
              className={tw`flex items-center gap-2 bg-primary text-white px-6 py-2 rounded-md ${
                sending || attachedFiles.some((f) => f.uploading)
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
            >
              {sending
                ? "Sending..."
                : attachedFiles.some((f) => f.uploading)
                ? "Uploading..."
                : "Send"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MailDetail;
